/**
 * web/src/components/ui/dropdown-menu.tsx
 * Radix DropdownMenu with token-overridden styling. Used by the sidebar
 * event selector (replaces the prototype's hand-rolled dropdown — gets
 * a11y for free) and the topbar admin overflow `…` menu.
 *
 * Pages MUST import from this wrapper, never @radix-ui/react-dropdown-menu
 * directly, so menu surface (rounded-lg, border, shadow-pop, fade-in
 * animation) stays consistent across the dashboard.
 */
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '../../lib/cn';

/** Root context provider — controls open state. */
export const DropdownMenu = DropdownMenuPrimitive.Root;

/** Element that toggles the menu. Wrap with asChild to use a custom button. */
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

/** Portals the menu to document.body so it escapes parent overflow/transform. */
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

/**
 * Menu surface: rounded-lg + 1px border + bg + 4px padding + shadow-pop
 * + 4px side offset + fade-in on open. min-w prevents tiny menus.
 */
export const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-[200px] rounded-lg border border-border bg-bg p-1 shadow-pop',
        'data-[state=open]:animate-[fade-in_150ms_ease-out]',
        className,
      )}
      {...props}
    />
  </DropdownMenuPortal>
));
DropdownMenuContent.displayName = 'DropdownMenuContent';

/**
 * Menu item — flex row with 8px gap + 6/10px padding + 13px text. Highlight
 * (data-[highlighted]) flips bg to bg-muted; disabled lowers opacity and
 * forbids cursor. Pass `onSelect` to handle activation.
 */
export const DropdownMenuItem = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'flex items-center gap-2 px-2.5 py-1.5 text-[13px] text-text rounded outline-none',
      'data-[highlighted]:bg-bg-muted data-[highlighted]:text-text cursor-pointer',
      'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = 'DropdownMenuItem';

/** Visual divider between menu groups — 1px bg-border with 4px vertical margin. */
export const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('h-px bg-border my-1', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';
